import React, {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AppContext } from "../store";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import DUOrder from "../pages/types/Order";
import EDIT_ORDER_MUTATION from "../orders/queries/EDIT_ORDER_MUTATION";
import { EventTypes, UITypes } from "../store/actions";
import { useMutation } from "@apollo/client";
import apolloClient from "../utils/apolloClient";
import { Link, matchPath, useNavigate, useLocation } from "react-router-dom";
import ORDERS_BY_STAKEHOLDER_QUERY from "../orders/queries/ORDERS_BY_STAKEHOLDER_QUERY";
import AlertDismissible from "./AlertDismissible";
import ShowHideAuth from "./ShowHideAuth";
import RDSelector from "./RDSelector";
import Loading from "./Loading";
import NewOrderButton from "../orders/components/newOrderButton";
import useShowDock from "../hooks/useShowDock";
import EVENT_AND_STAKEHOLDERS_QUERY from "../events/queries/EVENT_AND_STAKEHOLDERS_QUERY";
import { removeTypename } from "../utils";
import { useQueryParam } from "../hooks/useQueryParam";
import useIsEC from "../hooks/useIsEC";
import useUserEmail from "../hooks/useUserEmail"
import useEmulatedLookupID from "../hooks/useEmulatedLookupID"
import useIsFS from "../hooks/useIsFS"
import useOrgInfoLookupID from "../hooks/useOrgInfoLookupID"
import useOrgInfoVolunteerAccess from "../hooks/useOrgInfoVolunteerAccess"
import useEventInfo from "../hooks/useEventInfo"

const formatLocalDate = (dateStr: string | undefined) => {
  return dateStr ? new Date(dateStr).toLocaleDateString() : "";
};
const OrderDock = () => {
  const { dispatch, state } = useContext(AppContext);
  const userEmail = useUserEmail();
  const isEC = useIsEC();
  const location = useLocation();
  const { shown } = useShowDock();
  let query = useQueryParam();
  const [error, setError] = useState<string>();
  const [eventDetailError, setEventDetailError] = useState<boolean>(false);
  const [initialized, setInitialized] = useState(false);
  const [eventDetailsLoading, setEventDetailsLoading] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isFS = useIsFS();
  const emulatedLookupID = useEmulatedLookupID()
  const orgInfoLookupID = useOrgInfoLookupID();
  const lookupId = emulatedLookupID || orgInfoLookupID;
  const match = matchPath("/order/:orderId", pathname);
  let eventIdMatch = matchPath("/event/", pathname)
    ? (location.state as any)?.eventId
    : null;
  const eventInfo = useEventInfo();
  const orders = eventInfo.orders;
  const showAllOrders = state.uiInfo.display_all_orders;
  const setShowAllOrders = () => {
    dispatch({
      type: UITypes.setUI,
      payload: { ...state.uiInfo, display_all_orders: !state.uiInfo.display_all_orders },
    });
  }
  const currentOrder = eventInfo.currentOrder;
  const eventId =
    eventIdMatch ||
    query.get("eventId") ||
    eventInfo.currentEvent?.eventId ||
    currentOrder?.eventId;
  const chapterId =
    eventInfo.currentEvent?.chapterId ||
    currentOrder?.chapterId ||
    eventInfo?.currentEventDetail?.ChapterId;
  const currentEvent = useMemo(
    () =>
      eventId
        ? {
          eventId,
          chapterId,
          stakeholders: null,
          loading: false,
          loaded: false,
        }
        : null,
    [eventId, chapterId]
  );
  const payload = useMemo(
    () => ({
      currentEvent: {
        ...currentEvent,
        chapterId,
        eventId,
        stakeholders: currentEvent?.stakeholders || [],
      },
      loading: false,
      loaded: false,
    }),
    [currentEvent, eventId, chapterId]
  );
  const isVolunteer = useOrgInfoVolunteerAccess();
  const isOrderingPage = pathname.includes("/page/ordering");
  const isOrdersPage = pathname.includes("/orders/");
  const isDetailsPage = pathname.includes("/order/");
  const orderId = (match?.params as any)?.orderId;
  const orderNumber = query.get("orderNumber");
  const emulatedLookupId = useEmulatedLookupID();
  const setCurrentOrder = useCallback(
    (order: DUOrder | null) => {
      const event = {
        eventId: order?.eventId,
        chapterId: order?.chapterId,
        stakeholders: null,
        loaded: false,
        loading: false,
      };
      dispatch({
        type: EventTypes.SetOrders,
        payload: {
          orders: orders ? [...orders] : [],
          currentOrder: { ...order },
          currentEvent: { ...event },
        },
      });
      if (order && order.id) {
        navigate(`/order/${order.id}`);
      }
    },
    [dispatch, orders, navigate]
  );
  const getOrderById = (id: string, orderList: DUOrder[]) =>
    !id ? null : orderList.filter((o: DUOrder) => o.id === id)[0];
  const getOrdersByDateDesc = (orderList: DUOrder[]) =>
    [...orderList].sort(
      (a: DUOrder, b: DUOrder) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  const getMostRecentOrder = useCallback(
    (orderList: DUOrder[]) => getOrdersByDateDesc(orderList)[0],
    []
  );
  const getMostRecentEditableOrderByEventId = useCallback(
    (eventIdQuery: string, orderList: DUOrder[]) =>
      !eventIdQuery
        ? null
        : getOrdersByDateDesc(
          orderList.filter((o) => o.eventId === eventIdQuery)
        )[0],
    []
  );
  const getCurrentOrder = useCallback(
    (
      orderList: DUOrder[] = orders,
      currentOrderEventId: string = eventId,
      order: string = orderId
    ) => {
      if (order) {
        return getOrderById(order, orderList);
      } else if (currentOrderEventId) {
        return getMostRecentEditableOrderByEventId(
          currentOrderEventId,
          orderList
        );
      }
      return getMostRecentOrder(orderList);
    },
    [
      orders,
      eventId,
      orderId,
      getMostRecentOrder,
      getMostRecentEditableOrderByEventId,
    ]
  );


  const loadOrders = useCallback(async () => {
    const orderVariables: {
      lookupId?: string;
      orderNumber?: string;
      showAll?: boolean;
    } = {};
    if ((isEC || isFS) && !lookupId && !orderId) {
      return;
    }
    if (orderNumber) {
      orderVariables.orderNumber = orderNumber;
    }
    if (isEC || isFS) {
      orderVariables.lookupId = emulatedLookupId;
    }
    if (showAllOrders) {
      orderVariables.showAll = true;
    }
    try {
      const response = await apolloClient.query({
        variables: orderVariables,
        query: ORDERS_BY_STAKEHOLDER_QUERY,
        fetchPolicy: "no-cache",
      });


      const stakeholderOrders = response?.data?.ordersByStakeholder;
      const current = getCurrentOrder(stakeholderOrders);
      const orderPayload = {
        orders: [...stakeholderOrders],
        currentOrder: { ...current },
        loading: false,
        loaded: true,
      };
      dispatch({
        type: EventTypes.SetOrders,
        payload: orderPayload,
      });
    } catch (err) {
      console.error(err);
    }
  }, [
    orderId,
    dispatch,
    getCurrentOrder,
    lookupId,
    isEC,
    isFS,
    orderNumber,
    emulatedLookupId,
    showAllOrders,
  ]);


  const loadEventDetails = useCallback(async () => {
    try {
      setEventDetailsLoading(true);
      dispatch({
        type: EventTypes.SetOrders,
        payload: {
          currentEvent: { ...currentEvent, loading: true, loaded: false },
        },
      });
      const response = await apolloClient.query({
        variables: {
          eventId: eventId,
        },
        query: EVENT_AND_STAKEHOLDERS_QUERY,
        fetchPolicy: "no-cache",
      });
      const orderAndEventSummary = {
        currentEvent: {
          ...currentEvent,
          chapterId: response?.data?.event?.ChapterId,
          stakeholders: response?.data?.stakeholders,
          loading: false,
          loaded: true,
        },
      };
      dispatch({
        type: EventTypes.SetOrders,
        payload: orderAndEventSummary,
      });
      dispatch({
        type: EventTypes.SetDetails,
        payload: response.data?.event,
      });
      setEventDetailsLoading(false);
    } catch (err) {
      setEventDetailsLoading(false);
      setEventDetailError(true);
      setError("Your event details could not be found. Contact support.");
    }
  }, [eventId, currentEvent, dispatch]);


  const eventIsNew = eventId !== eventInfo.currentEvent?.eventId;
  const orderIsNew =
    orderId && currentOrder && currentOrder.id !== orderId && orders;
  const skip =
    (!isVolunteer && !isEC && !isFS) ||
    ((isEC || isFS) && !emulatedLookupId && !orderId) ||
    eventInfo.loading ||
    currentEvent?.loading ||
    eventInfo.loaded ||
    currentEvent?.loaded ||
    initialized;
  useEffect(() => {
    if (!skip) {
      setInitialized(true);
      if (!orders || !orders.length) {
        dispatch({
          type: EventTypes.SetOrders,
          payload: { ...payload, loading: true },
        });
        loadOrders();
      }
      if (eventId) {
        dispatch({
          type: EventTypes.SetOrders,
          payload: {
            ...payload,
            currentEvent: { ...currentEvent, loading: true },
          },
        });
        if (currentEvent && !currentEvent?.stakeholders && !eventDetailError) {
          loadEventDetails();
        }
      }
    } else {
      if (orderIsNew) {
        const order = getCurrentOrder(orders, eventId, orderId);
        let thisEvent = currentEvent;
        if (order && eventId !== order?.eventId) {
          thisEvent = {
            eventId: order.eventId,
            chapterId: order.chapterId,
            stakeholders: null,
            loaded: false,
            loading: false,
          };
        }
        dispatch({
          type: EventTypes.SetOrders,
          payload: { currentOrder: order, currentEvent: { ...thisEvent } },
        });
      }
      if (eventIsNew && !eventInfo.loading && eventId) {
        const order = getCurrentOrder(orders, eventId, orderId);
        dispatch({
          type: EventTypes.SetOrders,
          payload: {
            currentOrder: order,
            currentEvent: { ...currentEvent, loading: false, loaded: false },
            loaded: false,
          },
        });
      }
      if (
        eventId &&
        eventId !== eventInfo.currentEventDetail?.EventLookupId &&
        !eventDetailsLoading &&
        !eventDetailError
      ) {
        loadEventDetails();
      }
    }
  }, [
    currentEvent,
    currentOrder,
    dispatch,
    eventId,
    eventInfo,
    eventIsNew,
    orderIsNew,
    getCurrentOrder,
    loadOrders,
    orderId,
    orders,
    payload,
    skip,
    loadEventDetails,
    eventDetailsLoading,
    eventDetailError,
  ]);
  const orderShouldBeSet = orders && orders.length && orderId && !currentOrder;
  useEffect(() => {
    if (orderShouldBeSet) {
      const newOrder = orders.filter((o) => o.id === orderId)[0];
      if (newOrder) {
        setCurrentOrder(newOrder);
      }
    }
  }, [orderId, currentOrder, orders, setCurrentOrder, orderShouldBeSet]);
  const [editOrder] = useMutation(EDIT_ORDER_MUTATION);


  const addressIsComplete = () =>
    currentOrder?.address?.name &&
    currentOrder?.address?.street1 &&
    currentOrder?.address?.city &&
    currentOrder?.address?.region &&
    currentOrder?.address?.postal_code;


  const getOrderInput = (order: DUOrder) => {
    const input = { ...order } as DUOrder;
    delete (input as any).createdAt;
    delete (input as any).updatedAt;
    return removeTypename(input);
  };


  const submitForReview = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!currentOrder) return;
    let changedRecord: DUOrder | null = null;
    const edited = orders.map((o: DUOrder) => {
      if (currentOrder && currentOrder.id === o.id) {
        changedRecord = { ...currentOrder, state: "submitted" } as DUOrder;
        return changedRecord;
      }
      return o;
    });
    if (!changedRecord) return;
    try {
      await editOrder({
        variables: {
          orderData: getOrderInput(changedRecord),
        },
      });
      dispatch({
        type: EventTypes.SetOrders,
        payload: { orders: [...edited], currentOrder: changedRecord },
      });
    } catch (err: any) {
      setError(err.message);
    }
  };


  const placeOrder = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!currentOrder) return;
    let changedRecord: DUOrder | null = null;
    const edited = orders.map((o: DUOrder) => {
      if (currentOrder && currentOrder.id === o.id) {
        changedRecord = { ...currentOrder, state: "placed" } as DUOrder;
        return changedRecord;
      }
      return o;
    });
    if (!changedRecord) return;
    try {
      await editOrder({
        variables: {
          orderData: getOrderInput(changedRecord),
        },
      });
      dispatch({
        type: EventTypes.SetOrders,
        payload: { orders: [...edited], currentOrder: changedRecord },
      });
    } catch (err: any) {
      setError(err.message);
    }
  };


  const approveOrder = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!currentOrder) return;
    let changedRecord: DUOrder | null = null;
    const edited = orders.map((o: DUOrder) => {
      if (currentOrder && currentOrder.id === o.id) {
        changedRecord = { ...currentOrder, state: "approved" } as DUOrder;
        return changedRecord;
      }
      return o;
    });
    if (!changedRecord) return;
    try {
      await editOrder({
        variables: {
          orderData: getOrderInput(changedRecord),
        },
      });
      dispatch({
        type: EventTypes.SetOrders,
        payload: { orders: [...edited], currentOrder: changedRecord },
      });
    } catch (err: any) {
      setError(err.message);
    }
  };
  return shown && lookupId && (isVolunteer || isEC || isFS) ? (
    <Card className="mb-5">
      <Card.Header className="d-print-none">Orders</Card.Header>
      <Card.Body>
        {error && (
          <AlertDismissible handleClose={() => setError("")} variant="danger">
            {error}
          </AlertDismissible>
        )}
        <Row>
          <Col>
            {currentEvent && !currentEvent.eventId && (
              <p>
                No event selected. <Link to="/events/">View your events</Link>.
              </p>
            )}
            {currentEvent?.eventId && (
              <p className="m-0">
                {eventDetailsLoading && (
                  <>
                    <Loading />{" "}
                  </>
                )}
                {!eventDetailsLoading && (
                  <>
                    <strong>{eventInfo.currentEventDetail?.EventName}</strong>{" "}
                    on {eventInfo.currentEventDetail?.EventStartDate}{" "}
                  </>
                )}
                <span className="d-print-none">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      navigate("/events/");
                    }}
                  >
                    Select a new event
                  </Button>{" "}
                  or{" "}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      navigate("/orders/");
                    }}
                  >
                    See your orders
                  </Button>
                </span>
              </p>
            )}
          </Col>
        </Row>
        {(isEC || isFS) && (
          <Row className="d-print-none mt-3">  
            <Form.Group as={Col}>
              <RDSelector
                onChange={() => !isOrdersPage && navigate("/orders/")}
              />
            </Form.Group>
          </Row>
        )}


        {orders && !!orders.length && (
          <Row className="d-print-none mt-3">
            <Form.Group as={Col} xs={12} md={4}>
              <Form.Select
                onChange={(e) => {
                  const changedOrder = orders.filter((o) => o.id === e.target.value)[0];
                  setCurrentOrder(changedOrder ? changedOrder : null);
                }}
                value={currentOrder?.id || ""}
              >
                {!!orders.length && (
                  <option key="" value="">
                    --- None Selected ---
                  </option>
                )}
                {orders.map(
                  (o: DUOrder) =>
                    o && (
                      <option key={o.id} value={o.id}>
                        {formatLocalDate(o.createdAt)} - {o.description}
                      </option>
                    )
                )}</Form.Select>
              <Form.Check
                className="mb-3"
                style={{ marginTop: '1rem' }}
                type="checkbox"
                id="orderdock-allorders"
                label="Include orders from previous fiscal years"
                checked={showAllOrders}
                onChange={() => setShowAllOrders()}
              />
            </Form.Group>


            {currentOrder && currentOrder.id && (
              <>
                <Col xs={12} md={4}>
                  <p>
                    <strong>Order Number</strong> {currentOrder?.orderNumber}
                    <br />
                    <strong>Number of Items</strong>{" "}
                    {currentOrder?.items.reduce(
                      (acc, i) => i.quantity + acc,
                      0
                    )}
                    <br />
                    {!addressIsComplete() && !isDetailsPage && (
                      <span>
                        <Button
                          className="p-0"
                          variant="link"
                          onClick={() => navigate(`/order/${currentOrder?.id}`)}
                        >
                          Add a shipping address on details page
                        </Button>
                      </span>
                    )}
                    {!addressIsComplete() && isDetailsPage && (
                      <span className="text-danger">
                        Valid shipping address required to submit order.
                      </span>
                    )}
                  </p>
                </Col>
                <Col xs={12} md={4}>
                  {addressIsComplete() && currentOrder?.state === "new" && (
                    <ShowHideAuth
                      state={currentOrder.state}
                      stakeholders={currentOrder.stakeholders}
                      action="editOrderState"
                      email={userEmail}
                    >
                      <Button className="mb-2" onClick={submitForReview}>
                        Submit for Review
                      </Button>
                    </ShowHideAuth>
                  )}
                  {currentOrder?.state === "submitted" && (
                    <ShowHideAuth
                      state={currentOrder.state}
                      stakeholders={currentOrder.stakeholders}
                      action="editOrderState"
                      email={userEmail}
                    >
                      <Button className="mb-2" onClick={approveOrder}>
                        Mark Order Approved
                      </Button>
                    </ShowHideAuth>
                  )}
                  {currentOrder?.state === "approved" && (
                    <ShowHideAuth
                      state={currentOrder.state}
                      stakeholders={currentOrder.stakeholders}
                      action="editOrderState"
                      email={userEmail}
                    >
                      <Button className="mb-2" onClick={placeOrder}>
                        Mark Order Placed
                      </Button>
                    </ShowHideAuth>
                  )}
                  {!isDetailsPage && (
                    <div className="mb-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => navigate(`/order/${currentOrder?.id}`)}
                      >
                        Manage Order
                      </Button>
                    </div>
                  )}
                  {!isOrderingPage &&
                    ["new", "submitted"].includes(
                      currentOrder?.state || ""
                    ) && (
                      <div className="mb-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            navigate(
                              `/page/ordering?eventId=${currentOrder?.eventId}`
                            )
                          }
                        >
                          Add to Order
                        </Button>
                      </div>
                    )}
                  {!["new", "submitted"].includes(
                    currentOrder?.state || ""
                  ) && (
                      <div className="mb-2">
                        <NewOrderButton size="sm" variant="secondary">
                          Start a New Order
                        </NewOrderButton>
                      </div>
                    )}
                </Col>
              </>
            )}
          </Row>
        )}
        {(!orders || (orders && !orders.length)) && (
          <div>
            {" "}
            {!currentEvent && (
              <Button
                className="mt-3"
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  navigate("/events/");
                }}
              >
                Select an event to begin
              </Button>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  ) : null;
};


export default OrderDock;


